import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import DashboardShell, { WidgetCard, DonutChart } from "@/components/app/DashboardShell";
import {
  TodaysScheduleWidget, TeamActivityWidget, OrgPreviewWidget,
  PerformanceSnapshotWidget, QrewInboxWidget,
} from "@/components/app/DashboardWidgets";

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [profile, setProfile] = useState(null);
  const [today, setToday] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [teamAttendance, setTeamAttendance] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [emp, ap, t, p, mine, pul, ann, att] = await Promise.all([
        user?.employee_id ? api.get(`/employees`, { params: { manager_id: user.employee_id } }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        api.get("/approvals/summary").catch(() => ({ data: { pending_for_me: 0 } })),
        api.get("/tasks", { params: { scope: "team" } }).catch(() => ({ data: [] })),
        user?.employee_id ? api.get(`/employees/${user.employee_id}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        api.get("/attendance/mine").catch(() => ({ data: { today: null } })),
        api.get("/dashboard/announcements").catch(() => ({ data: null })),
        api.get("/announcements").catch(() => ({ data: [] })),
        api.get("/attendance/today").catch(() => ({ data: [] })),
      ]);
      setTeam(emp.data || []);
      setPendingApprovals(ap.data?.pending_for_me || 0);
      setTasks(t.data || []);
      setProfile(p.data);
      setToday(mine.data?.today);
      setPulse(pul.data);
      setAnnouncements(ann.data || []);
      const attData = att.data;
      setTeamAttendance(Array.isArray(attData) ? attData : (attData?.records || []));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.employee_id]);

  const checkIn  = async () => { setBusy(true); try { await api.post("/attendance/checkin", {}); toast.success("Checked in"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const checkOut = async () => { setBusy(true); try { await api.post("/attendance/checkout", {}); toast.success("Checked out"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };

  const teamSize = team.length;
  const teamPresent = useMemo(() => {
    if (!Array.isArray(teamAttendance)) return 0;
    const ids = new Set(team.map(t => t.id));
    return teamAttendance.filter(a => ids.has(a.employee_id) && a.check_in).length;
  }, [team, teamAttendance]);

  const openTasks = tasks.filter(t => t.status !== "done").length;
  const overdueTasks = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== "done").length;
  const onLeave = Array.isArray(teamAttendance) ? teamAttendance.filter(a => a.status === "leave").length : 0;

  const kpis = [
    { label: "Team Members",   value: teamSize,                                delta: { tone: "good", label: "↑ 4% vs Feb 2026" } },
    { label: "Active Today",   value: teamPresent,                             sub: teamSize ? `${Math.round((teamPresent / teamSize) * 100)}% of team` : "" },
    { label: "On Leave",       value: onLeave,                                  sub: "today" },
    { label: "Pending Tasks",  value: openTasks,                                sub: "View all" },
    { label: "Overdue Tasks",  value: overdueTasks,                             sub: overdueTasks ? "needs follow-up" : "all clear" },
    { label: "Team Score",     value: "4.3/5",                                  delta: { tone: "good", label: "↑ 8% vs Feb 2026" } },
  ];

  const teamMissing = Math.max(0, teamSize - teamPresent - onLeave);

  return (
    <DashboardShell
      greeting={`Good ${greetingTime()}, ${user?.full_name?.split(" ")[0] || "Manager"}!`}
      subtitle="Here's what's happening with your team."
      user={user}
      profile={profile}
      today={today}
      quote={pulse?.quote}
      announcements={announcements}
      onCheckIn={checkIn}
      onCheckOut={checkOut}
      busy={busy}
      kpis={kpis}
      testId="manager-dashboard"
      extraRows={[
        [
          <TodaysScheduleWidget />,
          <TeamActivityWidget employees={team} />,
          <PerformanceSnapshotWidget overall={4.3} distribution={[40, 38, 18, 3, 1]} quarter="Q1 2026" />,
        ],
        [
          <QrewInboxWidget pending={pendingApprovals} items={[{ title: "Leave request", subtitle: "Awaiting review" }, { title: "Timesheet approval", subtitle: "Pending" }]} />,
          { node: <OrgPreviewWidget root={profile} leaders={team.slice(0, 4)} />, span: 2 },
        ],
      ]}
      slotLeft={
        <WidgetCard title="My Team Attendance" action={<Link to="/attendance" className="text-[11px] font-semibold text-violet-700 hover:underline">Today</Link>} testId="mgr-team-attendance">
          <div className="flex flex-col items-center py-2">
            <DonutChart
              size={140}
              data={[
                { label: "Present",  value: teamPresent || 1,  color: "#10B981" },
                { label: "Absent",   value: teamMissing,       color: "#EF4444" },
                { label: "On Leave", value: onLeave,           color: "#F59E0B" },
              ]}
              centerValue={teamSize}
              centerLabel="Members"
            />
            <div className="grid grid-cols-1 gap-1.5 mt-4 w-full">
              <Legend color="#10B981" label="Present"  value={`${teamPresent} (${teamSize ? Math.round((teamPresent / teamSize) * 100) : 0}%)`} />
              <Legend color="#EF4444" label="Absent"   value={`${teamMissing}`} />
              <Legend color="#F59E0B" label="On Leave" value={`${onLeave}`} />
            </div>
            <Link to="/attendance" className="text-[11px] font-semibold text-violet-700 hover:underline mt-3">View details</Link>
          </div>
        </WidgetCard>
      }
      slotMid={
        <WidgetCard title="My Team Tasks" action={<span className="text-[10px] uppercase font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">{openTasks} Pending</span>} testId="mgr-team-tasks">
          {tasks.length === 0 ? (
            <div className="text-[12px] text-slate-500 py-6 text-center">No tasks yet.</div>
          ) : (
            <div className="space-y-2.5">
              {tasks.slice(0, 4).map(t => (
                <div key={t.id} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center text-[11px] font-bold shrink-0">
                    {(t.title || "T")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-slate-900 truncate">{t.title}</div>
                  </div>
                  <div className="text-[10px] text-slate-500 whitespace-nowrap">
                    {t.due_date ? `Due ${dueLabel(t.due_date)}` : "—"}
                  </div>
                </div>
              ))}
              <Link to="/tasks" className="text-[11px] font-semibold text-violet-700 hover:underline mt-2 inline-block">View all tasks →</Link>
            </div>
          )}
        </WidgetCard>
      }
      slotRight={
        <WidgetCard title="My Team Timesheets" action={<span className="text-[10px] uppercase tracking-wider font-bold text-violet-700">This Week</span>} testId="mgr-team-timesheets">
          <div className="space-y-3">
            <TimesheetRow label="Submitted"     value="18 (75%)"  color="#10B981" />
            <TimesheetRow label="Pending"       value="6 (25%)"   color="#F59E0B" />
            <TimesheetRow label="Not Submitted" value="0 (0%)"    color="#EF4444" />
          </div>
          <Link to="/timesheet-approvals" className="text-[11px] font-semibold text-violet-700 hover:underline mt-4 inline-block">View timesheets →</Link>
        </WidgetCard>
      }
    />
  );
}

function greetingTime() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

function dueLabel(iso) {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff > 0) return `in ${diff} days`;
  return `${Math.abs(diff)}d ago`;
}

function Legend({ color, label, value }) {
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span className="text-slate-700 font-semibold flex-1">{label}</span>
      <span className="text-slate-500">{value}</span>
    </div>
  );
}

function TimesheetRow({ label, value, color }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      <div className="flex-1 text-[12.5px] font-semibold text-slate-700">{label}</div>
      <div className="text-[12px] text-slate-500 font-semibold">{value}</div>
    </div>
  );
}
